import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { configureAmplify } from '../../utils/cognito'
import { type TenantInfo } from '../../models/TenantInfo'
import { notification } from '../../components/Notifications'

export interface UseLoginFlowResult {
  email: string
  isTenantInfoPending: boolean
  tenantErrorMessage: any
  tenantInfo?: TenantInfo
  setEmail: (email: string) => void
  getTenantInformation: (arg?: string) => Promise<void>
  resetTenantInformation: () => void
}

// • Custom hook for the login flow that fetches tenant info (by tenantKey or email domain)
// • Caches tenant info indefinitely, persists it in localStorage, and configures Amplify/Cognito from the response
// • Exposes state + helpers to fetch/reset tenant information and show a user-friendly notification on errors

export const useTenantInformation = (): UseLoginFlowResult => {
  const [email, setEmail] = useState('')

  const {
    mutateAsync,
    data: tenantInfo,
    isPending: isTenantInfoPending,
    error: tenantErrorMessage,
    reset: resetTenantInformation
  } = useMutation({
    gcTime: Infinity,
    mutationFn: async (pathParam: string) => {
      const normalizedEmail = email.trim()
      const response = await axios.post<TenantInfo>(
        `/api/tenant_info/${pathParam}`,
        normalizedEmail ? { email: normalizedEmail } : undefined
      )
      localStorage.setItem('tenantKey', pathParam)
      localStorage.setItem('tenantInfo', JSON.stringify(response.data))
      configureAmplify(response.data)
      return response.data
    }
  })

  const getTenantInformation = async (selectedTenantKey?: string): Promise<void> => {
    try {
      const pathParam = selectedTenantKey || email.split('@')?.[1]
      await mutateAsync(pathParam)
    } catch (e) {
      console.error('Error fetching tenant info:', e)
      let errorMessage: string
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        errorMessage = e.response.data.error
      } else {
        errorMessage = e instanceof Error ? e.message : String(e)
      }

      notification(`${errorMessage}`, { variant: 'info', subTitle: ' You can select tenant from the list.' })
    }
  }
  return {
    email,
    setEmail,
    tenantInfo,
    isTenantInfoPending,
    tenantErrorMessage,
    getTenantInformation,
    resetTenantInformation
  }
}
